import { useMemo, useState } from "react";

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import type { PlaidAccount } from "react-plaid-link";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Account } from "~/v2/api/rest/account";
import {
  createStripeSubscription,
  type StripeCoupon,
} from "~/v2/api/rest/billing";
import { applyEliteMarketData } from "~/v2/api/rest/elite";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import type { MarketDataPlans } from "../../util";
import type { PaymentMethod, PaymentSchedule } from "../types";
import { usePlaidStripeLink } from "./usePlaidStripeLink";

type PlaidStripeLinkData = {
  token: string;
  plaidAccount: PlaidAccount;
};

interface UseMarketDataReviewControllerParams {
  isElite: boolean;
  account: Account;
  paymentSchedule: PaymentSchedule;
  plans: MarketDataPlans;
  appliedCoupon: StripeCoupon | null;
  onFinished: () => void;
}

export const useMarketDataReviewController = ({
  isElite,
  account,
  paymentSchedule,
  plans,
  appliedCoupon,
  onFinished,
}: UseMarketDataReviewControllerParams) => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const isBankAccountPaymentEnabled = useFeatureEnabled(
    FeatureFlags.MARKET_DATA_BANK_ACCOUNT_PAYMENT,
  );

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("stripe");

  const [stripeCCError, setStripeCCError] = useState<string | null>(null);

  const stripe = useStripe();
  const elements = useElements();

  const [plaidStripeLinkData, setPlaidStripeLinkData] =
    useState<PlaidStripeLinkData | null>(null);

  const queryClient = useQueryClient();

  const {
    isReady: isPlaidStripeLinkReady,
    isLoading: isPlaidStripeLinkLoading,
    onOpenPlaidStripeLink,
  } = usePlaidStripeLink({
    onSuccess: ({ token, plaidAccount }) => {
      setPlaidStripeLinkData({ token, plaidAccount });
    },
    onError: (errorMessage) => {
      toastError(errorMessage);
    },
  });

  const submitPaidSubscriptionMutation = useMutationRequest(
    ({
      token,
      planId,
      couponId,
    }: {
      token: string;
      planId: string;
      couponId?: string;
    }) => createStripeSubscription({ token, planId, couponId }),
    {
      onSuccess: () => {
        AmplitudeProvider.dispatch("market_data_enrolled");
        toastSuccess("You have successfully subscribed to Algo Trader Plus.");
        setIsSuccessModalOpen(true);
      },
      onError: (error) => {
        console.error("error", error);
      },
    },
  );

  // If paying with CC we need to create a Stripe token before submitting the subscription
  const createStripeTokenMutation = useMutationRequest(
    () => {
      if (!stripe) {
        throw new Error("Stripe is null");
      }
      if (!elements) {
        throw new Error("Stripe Elements is null");
      }
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card Element is null");
      }
      return stripe.createToken(cardElement);
    },
    {
      onSuccess: (tokenResult) => {
        if (tokenResult.error) {
          setStripeCCError(
            tokenResult.error.message ??
              "Stripe ran into an error, please check input and try again.",
          );
          return;
        }

        if (!tokenResult.token) {
          setStripeCCError(
            "Stripe ran into an issue, please refresh page and try again.",
          );
          return;
        }

        setStripeCCError(null);

        submitPaidSubscriptionMutation.mutate({
          token: tokenResult.token.id,
          planId:
            paymentSchedule === "monthly" ? plans.monthly.id : plans.yearly.id,
          couponId: appliedCoupon?.id,
        });
      },
      onError: (error) => {
        setStripeCCError(
          "Stripe ran into an error, please refresh page and try again.",
        );
        console.error("createStripeTokenMutation error: ", error);
      },
    },
  );

  const eliteSubscriptionMutation = useMutationRequest(
    () => applyEliteMarketData(account.id),
    {
      onSuccess: () => {
        AmplitudeProvider.dispatch("market_data_enrolled");
        toastSuccess("You have successfully subscribed to Algo Trader Plus.");
        setIsSuccessModalOpen(true);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        console.error("Failed to subscribe to Algo Trader Plus", error);
        toastError(`Failed to subscribe to Algo Trader Plus: ${errorMessage}`);
      },
    },
  );

  const onSubscribeClicked = () => {
    if (isElite) {
      eliteSubscriptionMutation.mutate(true);
      return;
    }

    if (paymentMethod === "plaid") {
      // Edge case: button should be disabled if Plaid data does not exists
      if (!plaidStripeLinkData?.token || !plaidStripeLinkData?.plaidAccount) {
        toastError("Please link a bank account to continue");
        return;
      }

      submitPaidSubscriptionMutation.mutate({
        token: plaidStripeLinkData.token,
        planId:
          paymentSchedule === "monthly" ? plans.monthly.id : plans.yearly.id,
        couponId: appliedCoupon?.id,
      });
    } else if (paymentMethod === "stripe") {
      createStripeTokenMutation.mutate(true);
    }
  };

  const onStripeCcChange = () => {
    // Clear errors when user makes changes to CC input
    if (stripeCCError) {
      setStripeCCError(null);
    }
  };

  const onSuccessModalClose = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.marketDataSubscription(),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    onFinished();
  };

  const isSubscribeLoading =
    submitPaidSubscriptionMutation.isPending ||
    createStripeTokenMutation.isPending ||
    eliteSubscriptionMutation.isPending;

  const isSubscribeDisabled = useMemo(() => {
    if (isElite) {
      return eliteSubscriptionMutation.isPending;
    }

    if (paymentMethod === "plaid") {
      return !plaidStripeLinkData?.token || !plaidStripeLinkData?.plaidAccount;
    }

    // paymentMethod is Stripe CC
    return !stripe || !elements;
  }, [
    isElite,
    paymentMethod,
    plaidStripeLinkData,
    stripe,
    elements,
    eliteSubscriptionMutation.isPending,
  ]);

  const nextBillingDate = moment()
    .add(1, paymentSchedule === "yearly" ? "year" : "month")
    .format("MM/DD/YYYY");

  return {
    stripe,
    elements,
    isBankAccountPaymentEnabled,
    paymentMethod,
    onPaymentMethodChange: setPaymentMethod,
    nextBillingDate,
    isSubscribeLoading,
    isSubscribeDisabled,
    onSubscribeClicked,
    plaidStripeLink: {
      isReady: isPlaidStripeLinkReady,
      isLoading: isPlaidStripeLinkLoading,
      onOpen: onOpenPlaidStripeLink,
      token: plaidStripeLinkData?.token,
      account: plaidStripeLinkData?.plaidAccount,
    },
    stripeCCError,
    onStripeCcChange,
    isSuccessModalOpen,
    onSuccessModalClose,
  };
};

export type ReviewControllerReturn = ReturnType<
  typeof useMarketDataReviewController
>;
